// Calendario per-tecnico con drag-and-drop dei ticket
import { useState, useCallback, useMemo } from 'react'
import { Calendar, dateFnsLocalizer, Views, type SlotInfo } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import { format, parse, startOfWeek, getDay, addHours } from 'date-fns'
import { it } from 'date-fns/locale/it'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, CalendarDays, User } from 'lucide-react'
import api from '../../lib/api'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css'

// ── Localizer ──────────────────────────────────────────────────────────────
const locales = { it }
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { locale: it }),
  getDay,
  locales,
})

const DnDCalendar = withDragAndDrop(Calendar)

// ── Types ──────────────────────────────────────────────────────────────────
interface Technician {
  id: string
  firstName: string
  lastName: string
  email: string
}

interface TicketItem {
  id: string
  title: string
  status: string
  priority: string
  scheduledStart: string | null
  scheduledEnd: string | null
  assignedTo: string | null
}

interface CalendarEvent {
  id: string
  title: string
  start: Date
  end: Date
  resource: TicketItem
}

// ── Color by priority ───────────────────────────────────────────────────────
const PRIORITY_COLOR: Record<string, string> = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#2563eb',
  low: '#16a34a',
}

const STATUS_OPACITY: Record<string, string> = {
  completed: '0.5',
  cancelled: '0.35',
}

// ── Messages italiani ──────────────────────────────────────────────────────
const messages = {
  allDay: 'Tutto il giorno',
  previous: 'Prec',
  next: 'Succ',
  today: 'Oggi',
  month: 'Mese',
  week: 'Settimana',
  day: 'Giorno',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Ora',
  event: 'Ticket',
  noEventsInRange: 'Nessun ticket in questo periodo',
}

// ── Component ──────────────────────────────────────────────────────────────
export function TechnicianCalendarPage() {
  const qc = useQueryClient()
  const [selectedTech, setSelectedTech] = useState<string>('all')
  const [view, setView] = useState(Views.WEEK)
  const [date, setDate] = useState(new Date())

  // Fetch technicians
  const { data: technicians = [] } = useQuery<Technician[]>({
    queryKey: ['technicians'],
    queryFn: () => api.get<Technician[]>('/technicians').then(r => r.data),
  })

  // Fetch tickets for selected tech (or all assigned tickets)
  const { data: ticketData } = useQuery<{ items: TicketItem[] }>({
    queryKey: ['tickets-calendar', selectedTech],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '500' })
      if (selectedTech !== 'all') params.set('assignedTo', selectedTech)
      return api.get<{ items: TicketItem[] }>(`/tickets?${params}`).then(r => r.data)
    },
  })

  // Reschedule mutation
  const reschedule = useMutation({
    mutationFn: ({ id, scheduledStart, scheduledEnd }: { id: string; scheduledStart: Date; scheduledEnd: Date }) =>
      api.patch(`/tickets/${id}/reschedule`, {
        scheduledStart: scheduledStart.toISOString(),
        scheduledEnd: scheduledEnd.toISOString(),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tickets-calendar'] }),
  })

  // Build calendar events from tickets with scheduled dates
  const events = useMemo<CalendarEvent[]>(() => {
    if (!ticketData?.items) return []
    return ticketData.items
      .filter(t => t.scheduledStart)
      .map(t => {
        const start = new Date(t.scheduledStart!)
        const end = t.scheduledEnd
          ? new Date(t.scheduledEnd)
          : addHours(start, 1)
        return { id: t.id, title: t.title, start, end, resource: t }
      })
  }, [ticketData])

  // Drag-and-drop handler
  const onEventDrop = useCallback(
    ({ event, start, end }: { event: CalendarEvent; start: Date | string; end: Date | string }) => {
      reschedule.mutate({
        id: event.id,
        scheduledStart: new Date(start),
        scheduledEnd: new Date(end),
      })
    },
    [reschedule],
  )

  // Resize handler
  const onEventResize = useCallback(
    ({ event, start, end }: { event: CalendarEvent; start: Date | string; end: Date | string }) => {
      reschedule.mutate({
        id: event.id,
        scheduledStart: new Date(start),
        scheduledEnd: new Date(end),
      })
    },
    [reschedule],
  )

  // Event style
  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    const color = PRIORITY_COLOR[event.resource.priority] ?? '#2563eb'
    const opacity = STATUS_OPACITY[event.resource.status] ?? '1'
    return {
      style: {
        backgroundColor: color,
        borderColor: color,
        opacity,
        borderRadius: '4px',
        color: '#fff',
        fontSize: '12px',
        padding: '2px 6px',
      },
    }
  }, [])

  const selectedTechName = useMemo(() => {
    if (selectedTech === 'all') return 'Tutti i tecnici'
    const t = technicians.find(x => x.id === selectedTech)
    return t ? `${t.firstName} ${t.lastName}` : ''
  }, [selectedTech, technicians])

  return (
    <div className="flex flex-col h-full gap-4 p-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--fc-foreground)] flex items-center gap-2">
            <CalendarDays className="h-6 w-6 text-[var(--fc-primary)]" />
            Calendario tecnici
          </h1>
          <p className="text-sm text-[var(--fc-foreground-muted)] mt-1">
            Visualizza e pianifica i ticket — trascina per ripianificare
          </p>
        </div>

        {/* Technician selector */}
        <div className="flex items-center gap-3">
          <User className="h-4 w-4 text-[var(--fc-foreground-muted)]" />
          <select
            value={selectedTech}
            onChange={e => setSelectedTech(e.target.value)}
            className="rounded-md border border-[var(--fc-border)] bg-[var(--fc-background)] px-3 py-2 text-sm text-[var(--fc-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--fc-primary)]"
          >
            <option value="all">Tutti i tecnici</option>
            {technicians.map(t => (
              <option key={t.id} value={t.id}>
                {t.firstName} {t.lastName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-[var(--fc-foreground-muted)]">
        <span className="font-medium">Priorità:</span>
        {Object.entries(PRIORITY_COLOR).map(([p, c]) => (
          <span key={p} className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: c }} />
            {p}
          </span>
        ))}
        <span className="ml-4 italic">Ticket senza data pianificata non compaiono nel calendario</span>
      </div>

      {/* Calendar */}
      <div className="flex-1 min-h-0 rounded-xl border border-[var(--fc-border)] bg-[var(--fc-background)] overflow-hidden shadow-sm">
        <DnDCalendar
          localizer={localizer}
          events={events}
          view={view}
          onView={v => setView(v as string)}
          date={date}
          onNavigate={setDate}
          onEventDrop={onEventDrop as never}
          onEventResize={onEventResize as never}
          resizable
          draggableAccessor={() => true}
          eventPropGetter={eventStyleGetter as never}
          messages={messages}
          culture="it"
          style={{ height: '100%', minHeight: 600 }}
          popup
          selectable
          onSelectEvent={(event) => {
            window.open(`/tickets/${(event as CalendarEvent).id}`, '_self')
          }}
          components={{
            toolbar: (props) => (
              <CustomToolbar {...props} techName={selectedTechName} />
            ),
          }}
        />
      </div>
    </div>
  )
}

// ── Custom toolbar ─────────────────────────────────────────────────────────
interface ToolbarProps {
  date: Date
  view: string
  views: string[]
  label: string
  onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY' | 'DATE') => void
  onView: (view: string) => void
  techName: string
}

function CustomToolbar({ date, view, views, label, onNavigate, onView, techName }: ToolbarProps) {
  const viewLabels: Record<string, string> = {
    month: 'Mese',
    week: 'Settimana',
    day: 'Giorno',
    agenda: 'Agenda',
  }

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--fc-border)] bg-[var(--fc-surface)] flex-wrap gap-2">
      {/* Nav */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onNavigate('PREV')}
          className="p-1.5 rounded hover:bg-[var(--fc-background)] transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => onNavigate('TODAY')}
          className="px-3 py-1 text-sm font-medium rounded border border-[var(--fc-border)] hover:bg-[var(--fc-background)] transition-colors"
        >
          Oggi
        </button>
        <button
          onClick={() => onNavigate('NEXT')}
          className="p-1.5 rounded hover:bg-[var(--fc-background)] transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold text-[var(--fc-foreground)] ml-2">{label}</span>
      </div>

      {/* Tech name */}
      <span className="text-sm text-[var(--fc-foreground-muted)]">{techName}</span>

      {/* View selector */}
      <div className="flex rounded-md border border-[var(--fc-border)] overflow-hidden text-sm">
        {(views as string[]).map(v => (
          <button
            key={v}
            onClick={() => onView(v)}
            className={`px-3 py-1.5 transition-colors ${
              view === v
                ? 'bg-[var(--fc-primary)] text-white'
                : 'bg-[var(--fc-background)] text-[var(--fc-foreground)] hover:bg-[var(--fc-surface)]'
            }`}
          >
            {viewLabels[v] ?? v}
          </button>
        ))}
      </div>
    </div>
  )
}
